"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import {
  AdvancedMarker,
  Map,
  useAdvancedMarkerRef,
  useMap,
} from "@vis.gl/react-google-maps";

import { IMAGES } from "@/assets/images";
import { getPublicEnv } from "@/runtime-utils/config/public-env";
import { Circle } from "./Circle";

type GoogleMapProps = {
  containerStyle?: React.CSSProperties;
  center?: { lat: number; lng: number };
  setCenter?: (center: { lat: number; lng: number }) => void;
  radius?: number;
  disabled?: boolean;
  additonalMarker?: { lat: number; lng: number };
};

const initialCenter = { lat: 34.0522, lng: -118.2437 }; // Los Angeles, CA
const initialRadius = 250; // in meters

const MAP_ID = getPublicEnv("NEXT_PUBLIC_GOOGLE_MAP_ID", "90f87356969d889c");

export default function GoogleMap({
  center = initialCenter,
  radius = initialRadius,
  setCenter,
  containerStyle,
  disabled = false,
  additonalMarker,
}: GoogleMapProps) {
  const [zoom, setZoom] = React.useState<number>(16);

  const getZoomFromRadius = (radius: number) => {
    // for radius = 50 meters, zoom = 16
    // now according to formula zoom = 16 - log2(radius / initialRadius)
    return 16 - Math.log2(radius / initialRadius);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setZoom(getZoomFromRadius(radius));
  }, [radius]);

  const map = useMap();
  const [markerRef, marker] = useAdvancedMarkerRef();

  useEffect(() => {
    if (!map) return;
    if (center?.lat && center?.lng) {
      map.setCenter(center);
    }
  }, [map, center]);

  useEffect(() => {
    if (!map || !center || !additonalMarker) return;

    const bounds = new google.maps.LatLngBounds();
    if (center?.lat && center?.lng) bounds.extend(center);
    if (additonalMarker?.lat && additonalMarker?.lng)
      bounds.extend(additonalMarker);

    map.fitBounds(bounds); // ensures both markers are visible with padding
  }, [map, center, additonalMarker]);

  // const [infoWindowShown, setInfoWindowShown] = useState(false);

  // clicking the marker will toggle the infowindow
  // const handleMarkerClick = useCallback(() => setInfoWindowShown((isShown) => !isShown), []);

  // if the maps api closes the infowindow, we have to synchronize our state
  // const handleClose = useCallback(() => setInfoWindowShown(false), []);

  return (
    <div style={{ height: "500px", width: "100%", ...containerStyle }}>
      <Map
        defaultZoom={16}
        zoom={zoom}
        onZoomChanged={({ detail }) => {
          setZoom(detail.zoom);
        }}
        reuseMaps
        defaultCenter={center}
        gestureHandling=""
        disableDefaultUI={false}
        mapId={MAP_ID} // optional for styling
      >
        {/* Custom Marker */}
        <AdvancedMarker
          ref={markerRef}
          position={center}
          draggable={!disabled}
          // onClick={handleMarkerClick}
          onDragEnd={(e) => {
            setCenter?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          }}
        >
          <Image src={IMAGES.HomePin} alt="marker" width={40} height={40} />
        </AdvancedMarker>

        {/* Additional Markers */}
        {additonalMarker?.lat && additonalMarker?.lng && (
          <AdvancedMarker
            key={`additional-marker`}
            position={additonalMarker}
            // icon={{
            //   url: "/marker-icon.png",
            //   scaledSize: new google.maps.Size(30, 30),
            // }}
          />
        )}

        {/* {infoWindowShown && (
          <InfoWindow
            headerDisabled
            headerContent={<h3>InfoWindow Header Content</h3>}
            anchor={marker}
            onClose={handleClose}
          >
            <SVG.Cross onClick={handleClose} width={20} height={20} />
            <p>Some arbitrary html to be rendered into the InfoWindow.</p>
          </InfoWindow>
        )} */}

        {/* Radius Circle */}
        <Circle
          radius={radius}
          center={center}
          strokeColor={"#0c4cb3"}
          strokeOpacity={1}
          strokeWeight={3}
          fillColor={"#3b82f6"}
          fillOpacity={0.3}
          editable={false}
          //   draggable
        />
      </Map>
    </div>
  );
}
